import { apiPostImageOnPresignedData, apiUpdateProductImageUpload } from './api';
import { createImageThumbnail, getRenderOriginalFile } from './jsHelper';
import {
  THUMBNAIL_MAX_HEIGHT_RESOLUTION,
  THUMBNAIL_MAX_WIDTH_RESOLUTION,
} from '../constants/constants';

/**
 * Writes the saved edits into the product image's own stored file.
 *
 * Saving in the editor only stores `edit_config` against the render, so the
 * product image's `image_file` and `thumbnail` keep the picture as it was
 * produced - the card shows the unedited version, and so does anything else
 * reading the stored file.
 *
 * This replaces both with the edited image, in place: the row keeps its id (so
 * ordering, collections and share links still resolve) and keeps pointing at
 * the original render, which is the base every future edit is applied to.
 *
 * `editedImageUrl` is the blob the editor has already rendered for the preview,
 * so nothing is redrawn here. When the edits have been reset there is nothing
 * to bake in and the render as produced goes back instead.
 *
 * Returns true when the stored file was replaced, so the caller knows whether
 * there is a new row worth refetching.
 */
export const uploadEditedProductImage = async ({ image, productId, editedImageUrl }) => {
  const hasEdits = !!Object.keys(image?.render_image_details?.edit_config || {}).length;
  const sourceUrl = hasEdits ? editedImageUrl : getRenderOriginalFile(image);

  if (!image?.id || !productId || !sourceUrl) return false;

  // no-store so the media is read as it stands now, not from a cached copy.
  const response = await fetch(sourceUrl, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Failed to read the edited image: ${response.status}`);
  const file = await response.blob();

  // The keys have to describe what is actually being uploaded, so they come
  // from the blob rather than from the original - the editor exports a png
  // render as png and a jpg one as jpg.
  const baseName = image?.name || `image_${image.id}`;
  const result = await apiUpdateProductImageUpload(image.id, {
    productId,
    imageKey: `${baseName}.${file?.type === 'image/png' ? 'png' : 'jpg'}`,
    thumbnailKey: `${baseName}.${file?.type === 'image/png' ? 'png' : 'jpg'}`
  });

  // The create call on this endpoint answers with { status, data }, so unwrap
  // the envelope when there is one.
  const presigned = result?.data || result;
  if (!presigned?.presigned_data) return false;

  const thumbnail = await createImageThumbnail({
    file,
    customWidth: THUMBNAIL_MAX_WIDTH_RESOLUTION,
    customHeight: THUMBNAIL_MAX_HEIGHT_RESOLUTION,
  });

  if (presigned?.thumbnail_presigned_data) {
    await apiPostImageOnPresignedData(thumbnail, presigned.thumbnail_presigned_data);
  } else {
    // Without a slot to put it in, the stored thumbnail keeps the unedited
    // picture - and the card reads that before image_file, so say so rather
    // than leaving it looking like the upload did nothing.
    console.warn(
      'No thumbnail_presigned_data in the response - the stored thumbnail still holds the unedited picture'
    );
  }

  await apiPostImageOnPresignedData(file, presigned.presigned_data);
  return true;
};
